import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { fmtDT } from "@/constants";
import { fmtN } from "./common";

const ZONE_META = {
  proche: { title: "Communes proches — 20 à 25 min de Lesménils", testid: "seo-zone-proche" },
  metz: { title: "Metz et son agglomération", testid: "seo-zone-metz" },
  nancy: { title: "Nancy et son agglomération", testid: "seo-zone-nancy" },
};

function CommuneRow({ c, zone, index }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-slate-100 last:border-0" data-testid={`seo-commune-${zone}-${index}`}>
      <button type="button" className="w-full text-left px-4 py-3 hover:bg-slate-50" onClick={() => setOpen(!open)} data-testid={`seo-commune-toggle-${zone}-${index}`}>
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm font-semibold">{c.nom}
            {c.zones_autres?.length > 0 && <span className="ml-1.5 text-[10px] font-bold text-slate-400">aussi : {c.zones_autres.join(", ")}</span>}
          </p>
          <p className="text-xs font-mono tabular-nums text-slate-500 shrink-0">
            {c.duration_min != null ? `${c.duration_min} min` : "durée n.c."}{c.population ? ` · ${fmtN(c.population)} hab.` : ""}
          </p>
        </div>
        <p className="text-xs mt-0.5">
          {c.gsc
            ? <span className="font-semibold text-emerald-700">{fmtN(c.gsc.impressions)} impressions · {fmtN(c.gsc.clicks)} clics mesurés</span>
            : <span className="font-semibold text-amber-700">Aucune donnée Google — opportunité estimée</span>}
        </p>
      </button>
      {open && (
        <div className="px-4 pb-3 space-y-1.5 text-xs text-slate-600" data-testid={`seo-commune-detail-${zone}-${index}`}>
          {c.gsc?.queries?.length > 0 && <p>Requêtes mesurées : {c.gsc.queries.join(" · ")}</p>}
          {c.pages?.length > 0 && <p className="break-all">Page(s) liée(s) : {c.pages.map((p) => p.replace("https://www.photobooth54.fr", "") || "/").join(" · ")}</p>}
          <p className="font-semibold text-slate-700">{c.reco}</p>
        </div>
      )}
    </div>
  );
}

export default function SeoLocal() {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState({});
  const load = useCallback(() => { api.get("/seo/local/zones").then((r) => setData(r.data)).catch(() => setData({ zones: { proche: [], metz: [], nancy: [] }, zones_computing: false, error: true })); }, []);
  useEffect(() => { load(); }, [load]);
  const recompute = async () => {
    await api.post("/seo/local/recompute");
    toast.success("Calcul des communes lancé — temps de trajet routiers réels, cela prend environ une minute");
    setTimeout(load, 20000);
    setTimeout(load, 60000);
    setTimeout(load, 120000);
  };
  if (!data) return <p className="text-sm text-slate-400">Chargement…</p>;
  if (data.error) return <p className="text-sm text-rose-600 rounded-xl border border-rose-200 bg-rose-50 p-4" data-testid="seo-local-error">Zones locales indisponibles pour le moment — le reste de l'application fonctionne normalement.</p>;
  const empty = Object.values(data.zones).every((z) => z.length === 0);
  return (
    <div className="space-y-4" data-testid="seo-local">
      <div className="rounded-2xl border border-slate-200 bg-white p-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="text-xs text-slate-500">
          <p className="font-semibold text-slate-700">📍 Mon référencement local — départ : Lesménils (54700)</p>
          <p>Temps de trajet routiers réels (OSRM). {data.zones_computed_at ? `Calculé le ${fmtDT(data.zones_computed_at)}.` : "Jamais calculé."}
            {data.gsc_period && ` Visibilité Google du ${data.gsc_period.start} au ${data.gsc_period.end}.`}</p>
        </div>
        <button data-testid="seo-local-recompute" onClick={recompute} disabled={data.zones_computing}
          className="rounded-xl bg-slate-900 text-white font-bold text-sm px-4 py-2.5 disabled:opacity-50">
          {data.zones_computing ? "Calcul en cours…" : "Recalculer les communes"}
        </button>
      </div>
      {empty && !data.zones_computing && (
        <p className="text-sm text-slate-500 rounded-xl border border-slate-200 bg-white p-4" data-testid="seo-local-empty">
          Zones non calculées — lancez « Recalculer les communes » pour identifier automatiquement les communes concernées.
        </p>
      )}
      {Object.entries(ZONE_META).map(([zone, meta]) => {
        const list = data.zones[zone] || [];
        const shown = expanded[zone] ? list : list.slice(0, 15);
        return (
          <section key={zone} className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid={meta.testid}>
            <p className="px-4 pt-4 text-sm font-bold">{meta.title} <span className="text-slate-400 font-normal">({list.length})</span></p>
            {list.length === 0 && <p className="px-4 pb-4 pt-1 text-sm text-slate-400">Aucune commune dans cette zone.</p>}
            <div className="mt-1">
              {shown.map((c, i) => <CommuneRow key={c.code} c={c} zone={zone} index={i} />)}
            </div>
            {list.length > 15 && (
              <button data-testid={`seo-zone-more-${zone}`} onClick={() => setExpanded({ ...expanded, [zone]: !expanded[zone] })}
                className="w-full py-2.5 text-xs font-bold text-sky-700 hover:bg-slate-50">
                {expanded[zone] ? "Réduire" : `Voir les ${list.length - 15} autres communes`}
              </button>
            )}
          </section>
        );
      })}
    </div>
  );
}
